export const BLANK_LABEL = "(Blank)"

const normalizeToLabel = (raw) => {
  if (raw === null || raw === undefined) return BLANK_LABEL
  if (typeof raw === "string" && (raw.trim() === "" || raw.trim() === "-")) return BLANK_LABEL
  return String(raw)
}

const arrayOrBlank = (arr) =>
  Array.isArray(arr) && arr.length ? arr.map((v) => normalizeToLabel(v)) : [BLANK_LABEL]

const STATUS_LABELS = {
  1: "Planning",
  2: "In progress",
  Full: "Full",
}

const getStatusLabel = (value) => {
  if (value === null || value === undefined || value === "") return BLANK_LABEL
  if (value === 1) return "Planning"
  if (value === 2) return "In progress"
  if (value === "Full") return "Full"
  if (typeof value === "string" && value.trim()) return value.trim()
  return STATUS_LABELS[value] ?? "Possible to fill"
}

const getRecommendLabel = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase()
  if (!normalized) return BLANK_LABEL
  return normalized === "yes" || normalized === "true" ? "Yes" : "No"
}

const getNotesLabels = (row) => {
  const parts = []
  if (Array.isArray(row?.notes)) {
    row.notes.forEach((n) => {
      if (n?.text) parts.push(n.text)
    })
  }
  if (row?.nextMonth?.notes) parts.push(row.nextMonth.notes)
  return parts.length ? [parts.join(" | ")] : [BLANK_LABEL]
}

// Per-column value extractors, only needed where the raw row field doesn't
// already map 1:1 to what's rendered on screen (e.g. status codes, arrays,
// nested nextMonth fields). Everything else falls back to row[key].
const COLUMN_VALUE_GETTERS = {
  teacher: (row) => arrayOrBlank(row.teacher),
  status: (row) => [getStatusLabel(row.status)],
  recommendToClose: (row) => [getRecommendLabel(row.recommendToClose)],
  apolloVintage: (row) => [normalizeToLabel(row.nextMonth?.apolloVintage ?? row.apolloVintage)],
  notes: (row) => getNotesLabels(row),
}

export const getColumnValues = (row, key) => {
  const getter = COLUMN_VALUE_GETTERS[key]
  if (getter) return getter(row)
  const raw = row?.[key]
  if (Array.isArray(raw)) return arrayOrBlank(raw)
  return [normalizeToLabel(raw)]
}

const naturalCompare = (a, b) => {
  if (a === BLANK_LABEL) return 1
  if (b === BLANK_LABEL) return -1
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
}

// Unique values (+ counts) for a column, computed from the given rows.
// Callers typically pass rows already filtered by every OTHER active
// column filter, so the list only shows values still reachable (Excel-style).
export const getUniqueOptions = (rows, key) => {
  const counts = new Map()
  ;(rows ?? []).forEach((row) => {
    getColumnValues(row, key).forEach((value) => {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    })
  })
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => naturalCompare(a.value, b.value))
}

const rowMatchesColumnFilter = (row, key, selected) => {
  if (!selected) return true
  const values = getColumnValues(row, key)
  return values.some((v) => selected.has(v))
}

// filters: { [columnKey]: Set<string> } — a column is only present here when
// it actually restricts something (i.e. not every value is selected).
export const applyColumnFilters = (rows, filters, excludeKey) => {
  const entries = Object.entries(filters ?? {}).filter(([key]) => key !== excludeKey)
  if (!Array.isArray(rows)) return []
  if (entries.length === 0) return rows
  return rows.filter((row) => entries.every(([key, selected]) => rowMatchesColumnFilter(row, key, selected)))
}

export const getActiveFilterCount = (filters) => Object.keys(filters ?? {}).length
